import React, {
  cloneElement,
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useState,
} from 'react';
import clsx from 'clsx';

import styles from './styles/Accordion.module.scss';
import { useToggle } from './useToggle';

/** The design system's disclosure fade, for the callers that want one rather than an instant toggle. */
export const ACCORDION_FADE_DURATION_MS = 300;

export type AccordionHeadingElement = 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span';

export type AccordionVariant = 'primary' | 'secondary' | 'bare' | 'nav';

/** A row in a `nav` item's panel. */
export type AccordionNavLink = {
  href: string;
  icon?: ReactNode;
  label: string;
  /** Carried through to `onLinkClick` so a caller can report a value that is not the visible label. */
  meta?: string;
};

export type AccordionItemProps = {
  animationDurationMs?: number;
  chevronStroke?: 'light' | 'bold';
  classNames?: {
    button?: string;
    content?: string;
    header?: string;
    heading?: string;
    wrapper?: string;
  };
  heading: ReactNode;
  /** The element wrapping the header row. `div`/`span` for a disclosure inside a nav or a list, where
   *  a heading would announce structure the surrounding landmark does not have. */
  headingAs?: AccordionHeadingElement;
  /** Leading slot before the heading, sized and aligned by the variant. The slot is rendered even
   *  when empty, so a row without an icon keeps its label aligned with the rows that have one. */
  icon?: ReactNode;
  /** Replaces the built-in chevron, for a glyph the design system cannot supply — one the host's
   *  theme swaps, say. Outside `nav`, overriding takes the rotation, its transition and the sizing
   *  with it: those belong to `.indicator`, which a replacement does not carry. In `nav` they are
   *  keyed on position (`> svg`) instead, so a replacement svg still gets them. */
  indicator?: ReactNode;
  indicatorSize?: 'xxsm' | 'xsm' | 'sm' | 'md';
  isDense?: boolean;
  isOpen?: boolean;
  /**
   * Whether the panel is a `region` landmark. Pass `false` where many of these render together: the
   * ARIA practices guide says to avoid the role past roughly six panels, and beyond that a page of
   * same-labelled disclosures fills the landmark list with entries a reader cannot tell apart.
   */
  isPanelLandmark?: boolean;
  /** Panel content as a link list. Supply this OR `children`, not both. */
  links?: AccordionNavLink[];
  onChange?: (value: boolean) => void;
  onLinkClick?: (link: AccordionNavLink) => void;
  testId?: string;
  /**
   * Accessible name for a toggle rendered BESIDE the heading rather than around it.
   *
   * Setting it splits the header row: the heading becomes static content and only a chevron-sized
   * button toggles. That is the only shape that admits an `<a>` in the header, which may not be
   * nested inside the `<button>` the unsplit row renders.
   */
  toggleLabel?: string;
  trackingAttribute?: string;
  /** This item's key in a controlled `Accordion`. Ignored when the parent is uncontrolled. */
  value?: string;
  /**
   * Repeated on the item so a STANDALONE `Accordion.Item` carries a look. A grouped item inherits
   * the parent's, and `primary`/`secondary` additionally key off the parent's class, so those two
   * are group-only in practice.
   */
  variant?: AccordionVariant;
} & PropsWithChildren;

const AccordionItem = ({
  animationDurationMs = 0,
  chevronStroke = 'bold',
  children,
  classNames,
  heading,
  headingAs: HeadingElement = 'h3',
  icon,
  indicator,
  indicatorSize = 'xsm',
  isDense = false,
  isOpen = false,
  isPanelLandmark = true,
  links,
  onChange,
  onLinkClick,
  testId,
  toggleLabel,
  trackingAttribute,
  variant,
}: AccordionItemProps) => {
  const { Indicator, headingProps, sectionProps, triggerClass } = useToggle({
    animationDurationMs,
    isOpen,
    isPanelLandmark,
    onChange,
    indicatorSize,
    stroke: chevronStroke,
  });

  const triggerProps = {
    ...(headingProps as HTMLAttributes<HTMLButtonElement>),
    ...(trackingAttribute ? { 'data-analytics': trackingAttribute } : {}),
    ...(testId ? { 'data-testid': testId } : {}),
    type: 'button' as const,
  };

  // The panel is named by `aria-labelledby` pointing at this id. In the unsplit shape the button IS
  // the heading, so it can carry it; in the split shape the button is a chevron with an aria-label,
  // and leaving the id there names the panel after the control that opens it — "Toggle Football
  // sub-categories, region" rather than "Football".
  const { id: headingId, ...splitTriggerProps } = triggerProps;

  return (
    <div
      className={clsx(
        styles.item,
        { [styles.dense]: isDense },
        variant && variant !== 'bare' && styles[variant],
        classNames?.wrapper,
      )}
      data-cxy-name="accordion"
    >
      <HeadingElement className={clsx(styles.header, { [styles.splitHeader]: toggleLabel }, classNames?.header)}>
        {toggleLabel ? (
          <>
            <span className={clsx(styles.heading, classNames?.heading)} id={headingId}>
              {icon !== undefined && <span className={styles.iconSlot}>{icon}</span>}
              {heading}
            </span>
            <button
              aria-label={toggleLabel}
              className={clsx(triggerClass, styles.toggle, classNames?.button)}
              {...splitTriggerProps}
            >
              {indicator ?? <Indicator />}
            </button>
          </>
        ) : (
          <button className={clsx(triggerClass, styles.trigger, classNames?.button)} {...triggerProps}>
            {/* The label gets a box of its own ONLY alongside an icon slot, where a row layout needs
                to flex it. Wrapping unconditionally re-flows every existing trigger: the anonymous
                text node it replaces is not an equivalent box, and `display: contents` does not make
                it one either — both moved the product-plus FAQ. */}
            {icon === undefined ? (
              heading
            ) : (
              <>
                <span className={styles.iconSlot}>{icon}</span>
                <span className={clsx(styles.headingText, classNames?.heading)}>{heading}</span>
              </>
            )}
            {indicator ?? <Indicator />}
          </button>
        )}
      </HeadingElement>

      <div
        {...sectionProps}
        className={clsx(styles.content, classNames?.content)}
        // Published so the caller's stylesheet can transition on it. The duration has to be known
        // here — it is how long the panel stays unhidden while closing — so a second copy in CSS
        // could only ever drift, and the panel would snap to display:none mid-transition.
        style={{ '--cxy-accordion-duration': `${animationDurationMs}ms` }}
      >
        {links ? (
          <ul className={styles.linkList}>
            {links.map(link => (
              <li key={`${link.href}-${link.label}`}>
                <a className={styles.link} href={link.href} onClick={() => onLinkClick?.(link)}>
                  <span className={styles.iconSlot}>{link.icon}</span>
                  <span className={styles.linkLabel}>{link.label}</span>
                </a>
              </li>
            ))}
          </ul>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

export type AccordionProps = {
  chevronStroke?: 'light' | 'bold';
  defaultOpen?: boolean;
  indicatorSize?: 'xxsm' | 'xsm' | 'sm' | 'md';
  isDense?: boolean;
  name: string;
  /** Fired with the newly open item's `value`, or `null` when the open item was closed. */
  onValueChange?: (value: string | null) => void;
  type?: 'single' | 'multiple';
  /**
   * The open item's `value`, keyed by the caller rather than by position. Passing it (including
   * `null`) makes the accordion controlled, and `type` is then necessarily `single`.
   */
  value?: string | null;
  variant?: AccordionVariant;
} & PropsWithChildren;

const Accordion = ({
  chevronStroke = 'bold',
  children,
  defaultOpen,
  indicatorSize = 'xsm',
  isDense = false,
  name,
  onValueChange,
  type = 'single',
  value,
  variant = 'primary',
}: AccordionProps) => {
  const shouldShowMultipleOpen = type === 'multiple';
  const isControlled = value !== undefined;

  const accordions = React.Children.toArray(children) as ReactElement<PropsWithChildren<AccordionItemProps>>[];
  const initialValue = defaultOpen
    ? accordions.map(() => true)
    : accordions.map(item => (item.props.isOpen ? true : false));

  const [accordionValue, setAccordionValue] = useState(initialValue);

  const setValue = (newValue: boolean, index: number) => {
    let value = [];
    if (!shouldShowMultipleOpen) {
      value = accordions.map(() => false);

      if (newValue === true) {
        value[index] = true;
      }
    } else {
      value = [...accordionValue];
      value[index] = newValue;
    }

    setAccordionValue(value);
  };

  return (
    <div
      className={clsx(styles.accordion, variant !== 'bare' && styles[variant])}
      data-cxy-name="accordion"
      data-testid="accordion-component"
    >
      {children &&
        accordions.map((child, index) =>
          // Cloned rather than rebuilt from named props: every prop the item declares reaches it,
          // so adding one to `AccordionItemProps` does not also require adding it here.
          cloneElement(child, {
            chevronStroke,
            indicatorSize,
            isDense,
            variant,
            isOpen: isControlled ? child.props.value === value : accordionValue[index],
            key: `${name}-item-${index}`,
            onChange: (next: boolean) => {
              child.props.onChange?.(next);

              if (isControlled) {
                onValueChange?.(next ? (child.props.value ?? null) : null);
              } else {
                setValue(next, index);
              }
            },
          }),
        )}
    </div>
  );
};

Accordion.Item = AccordionItem;

export { Accordion };
